/**
 * Texto rico como DADO, nunca como HTML.
 *
 * Nada no projeto usa `innerHTML`. Enunciados vao conter texto escrito por professores
 * e, no futuro, vindos de banco de dados; renderizar isso como HTML e uma porta de
 * injecao. Alem disso, `RichText` como dado permite: renderizar para DOM, para string
 * de impressao, e para texto puro (leitor de tela, exportacao) a partir da mesma fonte.
 */

export type Inline =
  | { readonly t: 'text'; readonly s: string }
  | { readonly t: 'strong'; readonly s: string }
  | { readonly t: 'em'; readonly s: string }
  /** Matematica em LaTeX, renderizada por KaTeX. */
  | { readonly t: 'math'; readonly tex: string }
  /** Valor com unidade; mantidos juntos para nao quebrar linha entre eles. */
  | { readonly t: 'unit'; readonly value: string; readonly unit: string }
  /** Referencia a um estimulo ("veja a Figura 1"). */
  | { readonly t: 'ref'; readonly stimulus: string; readonly label: string }
  /** Quebra de paragrafo. */
  | { readonly t: 'br' };

export type RichText = readonly Inline[];

export const txt = (s: string): Inline => ({ t: 'text', s });
export const strong = (s: string): Inline => ({ t: 'strong', s });
export const em = (s: string): Inline => ({ t: 'em', s });
export const tex = (s: string): Inline => ({ t: 'math', tex: s });
export const unit = (value: string, u: string): Inline => ({ t: 'unit', value, unit: u });
export const ref = (stimulus: string, label: string): Inline => ({ t: 'ref', stimulus, label });
export const br: Inline = { t: 'br' };

/** Atalho: transforma uma string simples em RichText. */
export const rt = (s: string): RichText => [txt(s)];

/** Versao em texto puro -- leitores de tela, exportacao, buscas e testes. */
export function toPlainText(r: RichText): string {
  return r
    .map((i) => {
      switch (i.t) {
        case 'text':
        case 'strong':
        case 'em':
          return i.s;
        case 'math':
          return i.tex;
        case 'unit':
          return `${i.value} ${i.unit}`;
        case 'ref':
          return i.label;
        case 'br':
          return '\n';
      }
    })
    .join('');
}

/** Comprimento visivel; usado na invariante de homogeneidade das alternativas. */
export function visibleLength(r: RichText): number {
  return toPlainText(r).trim().length;
}
